import { TRecursiveNonNullable } from '@api/Helper.types';
import {
    useGetCustomerState,
    useGetProductOffers,
    useSplitTests
} from '@api/lendingAPI/lapiHooks';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { DEOnly } from '@iwIntlProvider/CountryWrappers';
import { tracking } from '@iwoca/frontend-tracking-library';
import { GetProductOffersResponse } from '@iwoca/lapi-client/edge';
import { Card, Link } from '@iwoca/orion';
import { getCurrentPathname } from '@iwRouter/LocationHelper';
import { NEW_IwLinkButton } from '@iwRouter/NEW_IwLinkButton';
import classNames from 'classnames';
import { startCase, upperFirst } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { twMerge } from 'tailwind-merge';

import {
    TProductOffersDocRequirementType,
    getOptionalDataRequirements,
    getSuggestedProduct
} from './ApplicationPendingView.helpers';
import messages from './ApplicationPendingView.messages';
import styles from './ApplicationPendingView.module.css';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import { MainSectionWrapper } from '../../../pageStructure/mainSectionWrapper';
import TradingHistory, {
    TGetProductOffersResponseData
} from '../../../ui/documentRequirement/TradingHistory';
import { LoadingAnimation } from '../../../ui/loadingAnimation/LoadingAnimation';
import ApplicationProgressBar from '../../../ui/progressBar/applicationProgressBar/ApplicationProgressBar';
import { PendingView } from '../PendingView';

const UK_SUPPORTED_DOCUMENTS: TProductOffersDocRequirementType[] = [
    'business_plan',
    'vat_return',
    'company_accounts',
    'management_accounts',
    'aged_debtor_report'
];

const DE_LEGAL_FORMS_THAT_ARENT_AUTO_LENDING_LEGIBLE: readonly string[] = [
    'gmbh',
    'ug',
    'gmbh_u_co_kg',
    'kg',
    'ag',
    'eg'
] as const;
type TGetProductOffersResponse =
    TRecursiveNonNullable<GetProductOffersResponse>;

type trackingApplicationStatus =
    | 'Application Submitted'
    | 'Documents Review'
    | 'Credit Analyst Review'
    | 'Your Decision';

export const mapProductStatusToStep = (
    status: TGetProductOffersResponse['data']['products'][number]['status']
) => {
    switch (status) {
        case 'accepted':
        case 'unknown':
            return 'applicationSubmitted';
        case 'pending_document_review':
        case 'pending_documents':
            return 'documentsReview';
        case 'credit_assessment_pending':
        case 'credit_assessment_required':
        case 'credit_assessment_amount_less_than_product_key':
            return 'creditAnalystReview';
        default:
            return 'yourDecision';
    }
};

export const ApplicationPendingView = () => {
    const stateKey = useStateKey();
    const businessDetailsReductionSplitTest = useSplitTests(
        'business_details_reduction'
    );
    const { data: customerStateResponse } = useGetCustomerState(stateKey);
    const showAddADGButton =
        DE_LEGAL_FORMS_THAT_ARENT_AUTO_LENDING_LEGIBLE.includes(
            customerStateResponse?.application?.company?.type
                ? customerStateResponse?.application?.company?.type
                : 'no_type_found'
        );

    const {
        data: productOffers,
        isLoading: isLoadingProductOffers,
        isError: isProductOffersError,
        isFetching: isFetchingProductOffers
    } = useGetProductOffers(stateKey);

    if (isLoadingProductOffers && isFetchingProductOffers)
        return (
            <MainSectionWrapper className={styles.wrapper}>
                <Card
                    className={styles.loadingContainer}
                    data-testid="application-loading"
                >
                    <LoadingAnimation />
                    <h2>
                        <FormattedMessage {...messages.loadingHeader} />
                    </h2>
                    <p>
                        <FormattedMessage {...messages.loadingDescription} />
                    </p>
                </Card>
            </MainSectionWrapper>
        );

    if (isProductOffersError) return <FallbackErrorModal />;

    const suggestedProduct = getSuggestedProduct(productOffers);

    // A suggested product should always be present in the backend for product_offer endpoint but handling the cases here when it's not loaded in properly.
    if (!suggestedProduct) return <PendingView />;

    const optionalDocs: ReturnType<typeof getOptionalDataRequirements> =
        getOptionalDataRequirements(suggestedProduct, UK_SUPPORTED_DOCUMENTS);

    const productOfferStatus = () => {
        return mapProductStatusToStep(
            suggestedProduct.status as TGetProductOffersResponse['data']['products'][number]['status']
        );
    };

    if (productOfferStatus && optionalDocs) {
        const optionalDocsList = optionalDocs.map((doc) => doc.type);

        tracking.ampli.applicationPendingPageViewed({
            'Aged debtor report required': optionalDocsList.includes(
                'aged_debtor_report'
            )
                ? 'Optional'
                : 'Not required',
            'Company accounts required': optionalDocsList.includes(
                'company_accounts'
            )
                ? 'Optional'
                : 'Not required',
            'Management accounts required': optionalDocsList.includes(
                'management_accounts'
            )
                ? 'Optional'
                : 'Not required',
            application_pending_status: startCase(
                upperFirst(productOfferStatus())
            ) as trackingApplicationStatus
        });
    }

    return (
        <MainSectionWrapper className={styles.wrapper}>
            <Card className={classNames(styles.card, styles.progress)}>
                <h2 className={styles.progressHeader}>
                    <FormattedMessage {...messages.title} />
                </h2>
                {productOfferStatus && (
                    <ApplicationProgressBar appStep={productOfferStatus()} />
                )}
                <p
                    className={styles.progressDescription}
                    data-testid="application-pending-view-description"
                >
                    <FormattedMessage
                        {...messages.description}
                        values={{
                            phoneNumber: (
                                <Link
                                    href={`tel:${
                                        getCountry() === 'DE'
                                            ? '06994323020'
                                            : messages.phoneNumber
                                                  .defaultMessage
                                    }`}
                                    onClick={() =>
                                        tracking.ampli.phoneNumberClicked({
                                            location_clicked_from: `Application progress bar: ${startCase(
                                                upperFirst(productOfferStatus())
                                            )}`,
                                            url: getCurrentPathname()
                                        })
                                    }
                                >
                                    <FormattedMessage
                                        {...messages.phoneNumber}
                                    />
                                </Link>
                            ),
                            emailAddress: getCountry() === 'DE' && (
                                <Link href="mailto:support@iwoca.de">
                                    support@iwoca.de
                                </Link>
                            )
                        }}
                    />
                </p>
            </Card>
            {((getCountry() === 'UK' &&
                productOfferStatus() === 'documentsReview' &&
                optionalDocs.length > 0) ||
                businessDetailsReductionSplitTest) && (
                <Card className={twMerge(classNames('mt-[15px] p-[25px]'))}>
                    <TradingHistory
                        optionalDocs={
                            optionalDocs as TGetProductOffersResponseData['products'][number]['data_requirements']
                        }
                    />
                </Card>
            )}
            <DEOnly>
                {productOfferStatus() === 'documentsReview' && (
                    <Card>
                        <div className="w-full text-left">
                            <h3>
                                <FormattedMessage
                                    id="_.pending_view.quick_actions.title"
                                    defaultMessage="Quick actions"
                                />
                            </h3>

                            <Card
                                className={twMerge(
                                    classNames('items-left mt-[15px] p-[25px]')
                                )}
                            >
                                <h3>
                                    <FormattedMessage
                                        id="_.pending_view.upload_documents.title"
                                        defaultMessage="Upload documents"
                                    />
                                </h3>
                                <div className="text-left">
                                    {suggestedProduct.status ===
                                        'pending_documents' && (
                                        <FormattedMessage
                                            {...messages.uploadDocumentsPendingDocsMessage}
                                        />
                                    )}
                                    {suggestedProduct.status ===
                                        'pending_document_review' && (
                                        <FormattedMessage
                                            {...messages.uploadDocumentsPendingDocReviewMessage}
                                        />
                                    )}
                                </div>
                                <NEW_IwLinkButton
                                    className={twMerge(
                                        classNames(
                                            'mt-[15px] w-full min-sm:max-w-[300px]'
                                        )
                                    )}
                                    to={{
                                        pathname: '/trading/'
                                    }}
                                    onClick={() =>
                                        tracking.ampli.uploadDocumentsCtaClicked(
                                            {
                                                suggested_product_status:
                                                    suggestedProduct.status ||
                                                    'undefined_status'
                                            }
                                        )
                                    }
                                >
                                    <FormattedMessage
                                        {...messages.uploadDocumentsButtonText}
                                    />
                                </NEW_IwLinkButton>
                            </Card>
                            {showAddADGButton && (
                                <Card>
                                    <div className="text-left">
                                        <h3>
                                            <FormattedMessage
                                                id="_.pending_view.add_guarantor.title"
                                                defaultMessage="Add an additional guarantor"
                                            />
                                        </h3>
                                        <FormattedMessage
                                            id="_.pending_view.add_guarantor.description"
                                            defaultMessage="If you have multiple directors and want to add an additional guarantor click the button below"
                                        />
                                    </div>
                                    <NEW_IwLinkButton
                                        className={twMerge(
                                            classNames(
                                                'mt-[15px] w-full min-sm:max-w-[300px]'
                                            )
                                        )}
                                        to={{
                                            pathname: '/company/'
                                        }}
                                    >
                                        <FormattedMessage
                                            id="_.pending_view.add_guarantor.button"
                                            defaultMessage="Go to your director list"
                                        />
                                    </NEW_IwLinkButton>
                                </Card>
                            )}
                        </div>
                    </Card>
                )}
            </DEOnly>
        </MainSectionWrapper>
    );
};
